import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { notifyAdminNewInquiry } from '../utils/notifications';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { formatUsd } from '../data/studyPlans';
import { useStudyPlans } from '../hooks/useStudyPlans';
import studentHero from '../assets/images/student-hero.png';

const CURRENT_YEAR = new Date().getFullYear();
const MATRIC_YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - i);

export default function ContactPage() {
  const location = useLocation();
  const { plans, demoVideoUrl } = useStudyPlans();
  const preselectedPlan = location.state?.planId || plans[0]?.id || '1-month';

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    matricYear: '',
    planId: preselectedPlan,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (location.state?.planId) {
      setForm((prev) => ({ ...prev, planId: location.state.planId }));
    } else if (plans.length && !plans.some((p) => p.id === form.planId)) {
      setForm((prev) => ({ ...prev, planId: plans[0].id }));
    }
  }, [location.state?.planId, plans, form.planId]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const fullName = form.fullName.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();
    const matricYear = form.matricYear;
    const planId = form.planId;
    const message = form.message.trim();

    if (!fullName || !email || !phone || !matricYear || !planId) {
      setError('Please complete all required fields.');
      return;
    }

      const plan = plans.find((p) => p.id === planId);
    setLoading(true);
    setError('');
    try {
      await addDoc(collection(db, 'registrationInquiries'), {
        fullName,
        email,
        phone,
        matricYear: Number(matricYear),
        planId,
        planLabel: plan?.name || planId,
        planPrice: plan?.price ?? null,
        message: message || '',
        status: 'new',
        createdAt: serverTimestamp(),
      });
      await notifyAdminNewInquiry({ fullName, email, phone }).catch(console.warn);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <MarketingLayout>
      <header className="mk-page-hero">
        <div className="mk-container">
          <span className="mk-eyebrow">Contact Us</span>
          <h1>Request enrollment</h1>
          <p>
            Share your details and preferred study plan. An advisor will confirm your admission fee
            and send your student login once enrollment is complete.
          </p>
        </div>
      </header>

      <section className="mk-section">
        <div className="mk-container mk-form-wrap">
          <aside className="mk-contact-aside">
            <h2 className="mk-h2" style={{ maxWidth: '12ch' }}>We are here to help</h2>
            <p>
              Prefer a conversation first? Submit the form and mention any questions -
              our team responds with next steps for payment and account access.
            </p>
            <div className="mk-contact-photo">
              <img src={studentHero} alt="Friendly student ready to start the GED program" />
            </div>
            <ul className="mk-contact-list">
              <li>
                <span className="material-symbols-outlined">schedule</span>
                <span>Advisor follow-up after you submit an enrollment request</span>
              </li>
              <li>
                <span className="material-symbols-outlined">key</span>
                <span>Login email &amp; password delivered after your plan is activated</span>
              </li>
              <li>
                <span className="material-symbols-outlined">payments</span>
                <span>
                  {plans[0]
                    ? `${plans[0].name}: ${formatUsd(plans[0].price)}${
                        plans[0].discountPercent > 0
                          ? ` (${plans[0].discountPercent}% off, was ${formatUsd(plans[0].regularPrice)})`
                          : ''
                      }`
                    : 'Ask us about current plan pricing'}
                </span>
              </li>
            </ul>
            {demoVideoUrl ? (
              <div style={{ marginTop: 20, padding: 16, background: '#f5f3ff', borderRadius: 12, border: '1px solid #ddd6fe' }}>
                <p style={{ fontWeight: 700, color: '#4b00d1', marginBottom: 8, fontSize: 14 }}>
                  Want to see how our AI system works?
                </p>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
                  Enroll now to watch the full demo video and unlock the complete GED AI learning portal.
                </p>
                <Link to="/signup" className="mk-btn mk-btn-primary" style={{ display: 'inline-flex' }}>
                  Enroll now to see demo
                </Link>
              </div>
            ) : null}
            <p style={{ marginTop: 28 }}>
              Already enrolled?{' '}
              <Link to="/login" style={{ color: 'var(--mk-primary)', fontWeight: 600 }}>
                Sign in here
              </Link>
            </p>
          </aside>

          <div className="mk-form">
            {submitted ? (
              <div className="mk-form-success">
                <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--mk-teal)' }}>
                  check_circle
                </span>
                <h3>Request received</h3>
                <p style={{ color: 'var(--mk-body)' }}>
                  Thank you. Our team will contact you shortly to complete enrollment and share your login details.
                </p>
                <Link to="/" className="mk-btn mk-btn-primary" style={{ marginTop: 20 }}>
                  Back to home
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && <div className="mk-form-error">{error}</div>}

                <div className="mk-field">
                  <label className="mk-label" htmlFor="fullName">Full name *</label>
                  <input
                    id="fullName"
                    className="mk-input"
                    value={form.fullName}
                    onChange={(e) => updateField('fullName', e.target.value)}
                    required
                    autoComplete="name"
                  />
                </div>

                <div className="mk-field">
                  <label className="mk-label" htmlFor="email">Email *</label>
                  <input
                    id="email"
                    type="email"
                    className="mk-input"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="mk-field">
                  <label className="mk-label" htmlFor="phone">Phone *</label>
                  <input
                    id="phone"
                    type="tel"
                    className="mk-input"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    required
                    autoComplete="tel"
                  />
                </div>

                <div className="mk-field">
                  <label className="mk-label" htmlFor="matricYear">Matric year *</label>
                  <select
                    id="matricYear"
                    className="mk-select"
                    value={form.matricYear}
                    onChange={(e) => updateField('matricYear', e.target.value)}
                    required
                  >
                    <option value="">Select year</option>
                    {MATRIC_YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <div className="mk-field">
                  <label className="mk-label" htmlFor="planId">Preferred study plan *</label>
                  <select
                    id="planId"
                    className="mk-select"
                    value={form.planId}
                    onChange={(e) => updateField('planId', e.target.value)}
                    required
                  >
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {formatUsd(p.price)}
                        {p.discountPercent > 0 ? ` (was ${formatUsd(p.regularPrice)})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mk-field">
                  <label className="mk-label" htmlFor="message">Message (optional)</label>
                  <textarea
                    id="message"
                    className="mk-textarea"
                    value={form.message}
                    onChange={(e) => updateField('message', e.target.value)}
                    placeholder="Questions about timing, payment, or which plan fits best…"
                  />
                </div>

                <button type="submit" className="mk-btn mk-btn-primary mk-btn-lg mk-btn-block" disabled={loading}>
                  {loading ? 'Sending…' : 'Submit enrollment request'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
